import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { PageHero } from "./PageHero";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./motion";
import { Seo } from "./Seo";

const SERVICE_SEO = {
  "general-contracting":
    "Full-service general contracting from Next Horizon Builders, with single-point responsibility, experienced field leadership, and cost and schedule accountability across Southern California.",
  "pre-construction":
    "Pre-construction services from Next Horizon Builders, including cost estimating, value engineering, constructability reviews, scheduling, and permitting coordination.",
  "ground-up-multifamily":
    "Ground-up multifamily construction by Next Horizon Builders, managing site work, structural construction, quality assurance, safety, and project closeout in Southern California.",
  "project-closeout":
    "Project closeout and turnover services from Next Horizon Builders, including punch list management, final inspections, documentation, and a seamless owner handover.",
};

export const ServiceDetail = ({ data }) => {
  const { hero, intro, cards, role, process, lists = [], cta, image, slug, name } = data;

  return (
    <>
      <Seo
        title={`${name} | Next Horizon Builders`}
        description={SERVICE_SEO[slug] || hero.description}
        path={`/services/${slug}`}
        image={image}
      />
      <PageHero
        eyebrow={hero.eyebrow}
        titleLines={hero.titleLines}
        tagline={hero.tagline}
        subtitle={hero.description}
        cta={hero.cta}
        image={image}
        imageAlt={`${name} — Next Horizon Builders, Southern California`}
      />

      {/* Intro */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow={intro.eyebrow} title={intro.title} />
          </div>
          <div className="lg:col-span-7 space-y-6 text-darkgray/75 text-lg leading-relaxed font-light">
            {intro.body.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className={i === intro.body.length - 1 && intro.body.length > 1 ? "text-navy font-normal" : ""}>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Cards */}
      {cards && (
        <section className="bg-lightgray py-24 md:py-36">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <SectionHeading eyebrow={cards.eyebrow} title={cards.title} />
            <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
              {cards.items.map((c, i) => (
                <Reveal key={c.title} delay={(i % 3) * 0.08}>
                  <div className="group bg-white p-9 md:p-11 h-full hover:bg-navy transition-colors duration-500">
                    <span className="font-serif text-2xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mt-8 font-serif font-light text-2xl md:text-3xl text-navy group-hover:text-white transition-colors duration-500 tracking-tight">{c.title}</h3>
                    <p className="mt-4 text-sm text-darkgray/60 group-hover:text-white/60 leading-relaxed font-light transition-colors duration-500">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Role */}
      {role && (
        <section className="bg-white py-24 md:py-32">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-20 items-start">
            <div className="lg:col-span-4">
              <SectionHeading eyebrow={role.eyebrow} title={role.title} />
            </div>
            <div className="lg:col-span-8">
              <Reveal>
                <p className="font-serif font-light italic text-navy text-2xl md:text-3xl leading-snug">{role.body}</p>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* Process */}
      {process && (
        <section className="bg-navy grain py-24 md:py-36 relative overflow-hidden">
          <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
            <SectionHeading light eyebrow={process.eyebrow} title={process.title} />
            <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
              {process.steps.map((s, i) => (
                <Reveal key={s.label} delay={(i % 3) * 0.08}>
                  <div className="bg-navy p-9 md:p-10 h-full">
                    <span className="font-serif text-4xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <p className="mt-6 text-[0.7rem] uppercase tracking-[0.2em] text-white/40 font-semibold">{s.label}</p>
                    {s.title && <h3 className="mt-2 font-serif font-light text-2xl text-white">{s.title}</h3>}
                    <p className="mt-3 text-white/60 leading-relaxed font-light text-sm">{s.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lists */}
      {lists.length > 0 && (
        <section className="bg-white py-24 md:py-36">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 space-y-20 md:space-y-28">
            {lists.map((list, li) => (
              <div key={li} className="grid lg:grid-cols-12 gap-10 lg:gap-20 items-start">
                <div className="lg:col-span-4">
                  <SectionHeading eyebrow={list.eyebrow} title={list.title} />
                </div>
                <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-5">
                  {list.items.map((item, i) => (
                    <Reveal key={item} delay={(i % 2) * 0.06}>
                      <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
                        <Check size={18} className="text-gold shrink-0 mt-1" />
                        <span className="text-darkgray font-light">{item}</span>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-navy py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-10">
          <div>
            <p className="eyebrow mb-5">Get Started</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-white tracking-tight max-w-xl leading-tight">{cta.title}</h2>
            <p className="mt-5 text-white/60 max-w-lg font-light leading-relaxed">{cta.body}</p>
          </div>
          <Reveal delay={0.1}>
            <Link
              to={cta.button.to}
              data-testid="service-cta"
              className="group inline-flex items-center gap-3 bg-gold text-white px-9 py-5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors duration-300 whitespace-nowrap"
            >
              {cta.button.label}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
};
